import { useEffect, useState } from "react";
import { getStore } from "store/storeUtils";
import Modal from "atoms/Modal";
import { TEAM_MODALS, TXN_MODALS } from "global/constants";
import { Text, Button, HStack, Radio, Input, VStack, Divider, useToast, Flex, Box } from "native-base";
import colors from "theme/colors";
import ReasonDropdown from "molecules/ReasonDropdown";
import { apiUpdateStatus, apiExport, apiToExportTransactionsList } from "api/apiCall";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import FilterContent from "molecules/FilterContent";
import { useTranslation } from "react-i18next";
import AmplitudeHelper from "utils/analytics";
import TxnPageFilterContent from "atoms/TxnPageFilterContent";
import { getFormattedEndDate, getFormattedStartDate } from "utils/helpers";
import { showCustomToast, showErrorToast } from "utils/utilities";
import { AiFillInfoCircle } from "react-icons/ai";

const TeamModal = ({ filterData = {}, setFilterData = () => {}, name = "", txnDates = {} }) => {
  const toast = useToast();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { modal, setModal, resetModal, resetLoginData } = getStore();
  const [status, setStatus] = useState("active");
  const [reason, setReason] = useState("");
  const [reasonDetail, setReasonDetail] = useState("");
  const [email, setEmail] = useState("");

  const bodyData = {
    employeeStatus: status === "active" ? "active" : "withdraw_suspended",
    suspendedReason: reasonDetail,
    suspensionCategory: reason,
  };
  const {
    isSuccess: statusSuccess,
    mutate: updateStatus,
    isError: isStatusError,
    error: statusError,
  } = useMutation({
    mutationFn: (id) => {
      return apiUpdateStatus({ employeeId: id ?? 0 }, bodyData);
    },
  });

  const {
    isSuccess: exportSuccess,
    mutate: performExport,
    isError: isExportError,
    data: exportData,
    error: exportError,
  } = useMutation({ mutationFn: () => apiExport({ email }) });

  const {
    mutate: performExportTransactions,
    isSuccess: exportTransactionListSuccess,
    data: transactionListData,
    isError: isExportTransactionListError,
    error: exportTransactionListError,
  } = useMutation({
    mutationFn: (payload) => apiToExportTransactionsList(payload, { email }),
  });

  useEffect(() => {
    if (modal?.data?.email && modal?.data?.email != "") {
      setEmail(modal?.data?.email);
    }
    if (modal?.data?.employeeStatus && modal?.data?.employeeStatus != "") {
      setStatus(modal?.data?.employeeStatus);
    }
  }, [modal]);

  useEffect(() => {
    if (exportTransactionListSuccess || exportSuccess) {
      showCustomToast({
        title: "transactionPage.downloadDataSuccess",
        status: "success",
        variant: "solid",
        toast,
      });
      resetModal();
    }
  }, [exportTransactionListSuccess, exportSuccess, transactionListData, exportData]);

  useEffect(() => {
    if (isExportError) {
      showErrorToast({ error: exportError, toast, navigate, resetLoginData });
    }
  }, [isExportError, exportError]);

  useEffect(() => {
    if (isStatusError) {
      showErrorToast({ error: statusError, toast, navigate, resetLoginData });
    }
  }, [isStatusError, statusError]);

  useEffect(() => {
    if (statusSuccess) {
      showCustomToast({
        title: "home.statusChange.success",
        status: "success",
        variant: "solid",
        toast,
      });
      resetModal();
    }
  }, [statusSuccess]);

  useEffect(() => {
    if (isExportTransactionListError) {
      showErrorToast({
        error: exportTransactionListError,
        toast,
        navigate,
        resetLoginData,
      });
      resetModal();
    }
  }, [isExportTransactionListError, exportTransactionListError]);

  const resetAndCloseModal = () => {
    resetModal();
  };

  const sendDownloadRequest = () => {
    const data = {};
    if (name != "") {
      data.search = name;
    }

    if (txnDates?.startDate != "" && txnDates?.endDate != "") {
      data.fromTransactionDate = getFormattedStartDate(txnDates?.startDate);
      data.toTransactionDate = getFormattedEndDate(txnDates?.endDate);
    }

    if (filterData?.fromTransactionAmount != "") {
      data.fromTransactionAmount = filterData?.minAmount;
    }
    if (filterData?.toTransactionAmount != "") {
      data.toTransactionAmount = filterData?.maxAmount;
    }
    if (filterData?.employeeGroup && filterData?.employeeGroup != []) {
      data.employeeGroupName = filterData?.employeeGroup.join(",");
    }
    if (filterData?.txnStatus != [] && filterData?.txnStatus) {
      data.transactionStatus = filterData?.txnStatus.join(",");
    }
    if (filterData.withdrawalPurpose != [] && filterData?.withdrawalPurpose) {
      data.transactionCategoryId = (filterData?.withdrawalPurpose || []).map((i) => i?.id).join(",");
    }
    AmplitudeHelper.logEvent("send_download_clicked", {
      source: "transaction_page",
      email_address: email,
    });
    if (!modal?.shouldFiltersApply) {
      performExportTransactions();
    } else {
      performExportTransactions(data);
    }
  };

  const setEmailAddress = (e) => {
    setEmail(e.target.value);
  };

  const shouldSaveBeDisabled = (status) => {
    if (modal?.data?.employeeStatus !== "" && modal?.data?.employeeStatus) {
      return status === modal?.data?.employeeStatus;
    }
    if (status === "inactive") {
      if (reason && !reason.includes("Other")) {
        return false;
      }
      if (reason.includes("Other") && reasonDetail.length > 0) {
        return false;
      }
      return true;
    }
    return status === "active";
  };

  const onChangeStatus = (status) => {
    AmplitudeHelper.logEvent(status == "active" ? "active_status_clicked" : "suspend_status_clicked", {
      source: "team_page",
      employee_status_input: status,
      employee_id: modal?.data?.employeeId,
    });
    setStatus(status);
    setReason("");
    setReasonDetail("");
    shouldSaveBeDisabled(status);
  };

  const setUserSelectedReason = (reason) => {
    setReason(reason);
    setReasonDetail("");
  };

  const setReasonDetailInput = (e) => {
    const inputValue = e.target.value;
    if (inputValue.length <= 100) {
      setReasonDetail(inputValue);
    } else {
      // Truncate the input to the first 100 characters
      setReasonDetail(inputValue.slice(0, 100));
    }
  };

  const RenderModal = () => {
    switch (modal.type) {
      case TEAM_MODALS.changeStatus:
        return (
          <Modal height="320px" title="Ubah Status">
            <VStack space="2" px="10px">
              <Text variant="xs">{t("modal.suspension.status")}</Text>
              <Radio.Group value={status} onChange={onChangeStatus}>
                <HStack space="3">
                  <HStack space={2} alignItems="center">
                    <Radio value="active">
                      <Text variant="xs">{t("modal.suspension.active")}</Text>
                    </Radio>
                  </HStack>
                  <HStack space={2} alignItems="center">
                    <Radio value="inactive">
                      <Text variant="xs">{t("modal.suspension.inactive")}</Text>
                    </Radio>
                  </HStack>
                </HStack>
              </Radio.Group>
              <Text variant="xs">{t("modal.suspension.reasonForInactivity")}</Text>
              <ReasonDropdown isDisabled={status !== "inactive"} reason={reason} setReason={setUserSelectedReason} />
              <Text variant="xs">{t("modal.suspension.writeDetailedReason")}</Text>
              <Input
                placeholder={t("modal.suspension.placeholder")}
                value={reasonDetail}
                isDisabled={status !== "inactive" || reason === "" || !reason.includes("Other")}
                onChange={setReasonDetailInput}
                onFocus={() => {
                  AmplitudeHelper.logEvent("detail_suspend_clicked", {
                    source: "team_page",
                    employee_id: modal?.data?.employeeId,
                  });
                }}
                onEndEditing={() => {
                  AmplitudeHelper.logEvent("detail_suspend_filled", {
                    source: "team_page",
                    employee_id: modal?.data?.employeeId,
                    detail_suspended: reasonDetail,
                  });
                }}
              />
              <Text alignSelf="flex-end" variant="xs" numberOfLines={1}>
                <Text color={reasonDetail.length >= 100 ? colors.error.rose : colors.secondary.orchid}>
                  {reasonDetail.length || 0}
                </Text>
                /100
              </Text>
            </VStack>

            <HStack space={2} width="100%" bottom="3" position="absolute" zIndex={-999} px="10px">
              <Button
                variant="outline"
                onPress={() => {
                  AmplitudeHelper.logEvent("status_changes_canceled", {
                    source: "team_page",
                    employee_id: modal?.data?.employeeId,
                  });
                  resetModal();
                }}
                flex="0.25"
              >
                <Text variant="xs">{t("modal.suspension.cancel")}</Text>
              </Button>
              <Button
                variant="solid"
                flex="0.75"
                isDisabled={shouldSaveBeDisabled(status)}
                onPress={() => {
                  AmplitudeHelper.logEvent("save_button_clicked", {
                    source: "team_page",
                    employee_id: modal?.data?.employeeId,
                  });
                  setModal({
                    ...modal,
                    type: TEAM_MODALS.singleUpdate,
                    data: { employeeId: modal?.data?.employeeId },
                  });
                }}
              >
                <Text variant="xs" color={colors.neutral.cotton}>
                  {t("modal.suspension.save")}
                </Text>
              </Button>
            </HStack>
          </Modal>
        );
      case TEAM_MODALS.singleUpdate:
        return (
          <Modal height="200px">
            <Flex p="10px" borderRadius={12} mb="10px" w="100%" alignItems="center">
              <HStack alignItems="center" space={3} style={{ flexWrap: "wrap" }}>
                <Box variant="ghost" alignSelf="flex-start">
                  <AiFillInfoCircle size="25px" color={colors.secondary.orchid} />
                </Box>
                <VStack style={{ flexWrap: "wrap", flex: 1 }}>
                  <Text variant="sm-bold">{t("modal.update.confirm")}</Text>
                  <Text variant="xs" noOfLines={10}>
                    {t("modal.update.confirmDesc")}
                  </Text>
                </VStack>
              </HStack>
              <HStack space={2} alignSelf="flex-end" position="absolute" bottom="-50">
                <Button
                  variant="outline"
                  borderColor={colors.neutral.darkMist}
                  px="10px"
                  onPress={() => {
                    AmplitudeHelper.logEvent("cancel_button_clicked", {
                      source: "continue_pop_up_confirmation",
                    });
                    resetModal();
                  }}
                >
                  <Text variant="xs" color={colors.neutral.darkMist}>
                    {t("modal.update.cancel")}
                  </Text>
                </Button>
                <Button
                  variant="solid"
                  px="30px"
                  onPress={() => {
                    AmplitudeHelper.logEvent("save_button_clicked", {
                      source: "continue_pop_up_confirmation",
                    });
                    updateStatus(modal?.data?.employeeId);
                    resetModal();
                  }}
                >
                  <Text variant="xs" color={colors.neutral.cotton}>
                    {t("modal.update.submit")}
                  </Text>
                </Button>
              </HStack>
            </Flex>
          </Modal>
        );

      case TEAM_MODALS.export:
        return (
          <Modal height="270px" title={t("modal.export.title")} reset={resetModal} closeable>
            <VStack space="3">
              <Divider />
              <VStack p="15px" space="3">
                <Text variant="xs">{t("modal.export.description")}</Text>
                <Input
                  placeholder={t("modal.export.placeholder")}
                  value={email}
                  onChange={(x) => setEmail(x.target.value)}
                  onEndEditing={() => {
                    AmplitudeHelper.logEvent("email_address_filled", {
                      source: "team_page",
                      email_address: email,
                    });
                  }}
                />
              </VStack>
              <Divider />
              <Button
                variant="solid"
                mx="15px"
                onPress={() => {
                  AmplitudeHelper.logEvent("send_download_clicked", {
                    source: "team_page",
                    email_address: email,
                  });
                  performExport();
                }}
                isDisabled={email == ""}
              >
                <Text variant="xs" color={colors.neutral.cotton}>
                  {t("modal.export.button")}
                </Text>
              </Button>
            </VStack>
          </Modal>
        );
      case TEAM_MODALS.filter:
        return (
          <Modal title="Pilih Bedasarkan" reset={resetModal} closeable>
            <FilterContent filterData={filterData} setFilterData={setFilterData} resetModal={resetModal} />
          </Modal>
        );
      case TXN_MODALS.export:
        return (
          <Modal height="270px" title={t("modal.export.title")} reset={resetAndCloseModal} closeable>
            <VStack space="3">
              <Divider />
              <VStack p="15px" space="3">
                <Text variant="xs">{t("modal.export.description")}</Text>
                <Input
                  placeholder={t("modal.export.placeholder")}
                  value={email}
                  onPressOut={() => {
                    AmplitudeHelper.logEvent("email_address_filled", {
                      source: "transaction_page",
                      email_address: email,
                    });
                  }}
                  onChange={setEmailAddress}
                  onEndEditing={() => {
                    AmplitudeHelper.logEvent("email_address_filled", {
                      source: "txn_page",
                      email_address: email,
                    });
                  }}
                />
              </VStack>
              <Divider />
              <Button variant="solid" mx="15px" onPress={sendDownloadRequest} isDisabled={email == ""}>
                <Text variant="xs" color={colors.neutral.cotton}>
                  {t("modal.export.button")}
                </Text>
              </Button>
            </VStack>
          </Modal>
        );
      case TXN_MODALS.filter:
        return (
          <Modal title="Pilih Bedasarkan" reset={resetModal} closeable>
            <TxnPageFilterContent filterData={filterData} setFilterData={setFilterData} resetModal={resetModal} />
          </Modal>
        );
      default:
        <></>;
    }
  };

  return <>{RenderModal()}</>;
};

export default TeamModal;
